import { Controller } from "@hotwired/stimulus"
import { isHTMLElement } from "~/types/types.util"
import * as bootstrap from "bootstrap"
import { isTouchDevice } from "~/helpers/dom_helpers"

// Connects to data-controller="title-tooltip"
export default class TitleTooltipController extends Controller<HTMLBodyElement> {
  private observer?: MutationObserver

  connect() {
    // We completely disable tooltips on touch devices
    if (isTouchDevice()) return

    // Initialize tooltips on the current elements of the body
    this.#installTooltips(this.element)

    // Observe any new elements that are added to the body element and install tooltips on them
    this.observer = new MutationObserver(this.#handleNewElements.bind(this))
    this.observer.observe(this.element, { childList: true, subtree: true })
  }

  disconnect() {
    // Restore original title attributes when the controller is disconnected
    this.#disposeTooltips()
    this.observer?.disconnect()
  }

  #handleNewElements(mutations: MutationRecord[]) {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (isHTMLElement(node)) {
          this.#installTooltips(node)
        }
      })
    })
  }

  #installTooltips(node: HTMLElement) {
    node.querySelectorAll<HTMLElement>("[title]:not([data-disable-title-tooltip])").forEach((tooltipTriggerEl) => {
      const tooltip = new bootstrap.Tooltip(tooltipTriggerEl, {
        fallbackPlacements: ["bottom", "top", "right", "left"],
        placement: "bottom",
        trigger: "hover focus",
        delay: { show: 750, hide: 0 },
      })
      const hideNow = () => tooltip.hide()

      tooltipTriggerEl.addEventListener("hide.bs.tooltip", (e) => {
        // Assign current tooltip ID to tooltipId variable
        const tooltipId = tooltipTriggerEl.getAttribute("aria-describedby")

        const currentTooltip = document.querySelector<HTMLElement>(`#${tooltipId}`)

        if (currentTooltip && currentTooltip.matches(":hover")) {
          e.preventDefault()
          currentTooltip.addEventListener("mouseleave", hideNow, { once: true })
        }
      })
    })
  }

  #disposeTooltips() {
    const dataAttributeName = "data-bs-original-title"
    this.element.querySelectorAll(`[${dataAttributeName}]`).forEach((el) => {
      // Revert to original title attribute
      const originalTitle = el.getAttribute(dataAttributeName)
      if (originalTitle) el.setAttribute("title", originalTitle)
      el.removeAttribute(dataAttributeName)

      // Dispose of the Bootstrap tooltip instance
      bootstrap.Tooltip.getInstance(el)?.dispose()
    })
  }
}
